/**
 * Guides for every part of OgreClan's draft leagues, in one place: the site, the Discord bot (for players and for
 * moderators), the league spreadsheet, play.ogreclan.com, replays, and staff. Written from the bot's PLAYERS.md,
 * MODERATORS.md and README, the Showdown draft league guide, and the site's own guide; keep them in step.
 *
 * One thing is for staff only, as MODERATORS.md asks: that /myqueue in a league's admin channel looks up anyone's
 * queue. It shows only to the site's staff.
 */
import type { ComponentChildren } from 'preact';
import { signInUrl } from '../api';
import { useMe } from '../me';
import { Link } from '../router';
import { draftUrl, HOME_HOST } from '../sites';
import { toast } from '../ui';

const SECTIONS = [
	['start', 'Start here'],
	['site', 'The website'],
	['bot', 'Bot: players'],
	['moderators', 'Bot: moderators'],
	['sheet', 'The spreadsheet'],
	['showdown', 'Showdown'],
	['replays', 'Replays'],
	['staff', 'Staff and admins'],
] as const;

function Table({ head, rows }: { head: string[], rows: ComponentChildren[][] }) {
	return <div class="table-wrap"><table class="guide-table">
		<thead><tr>{head.map(h => <th key={h}>{h}</th>)}</tr></thead>
		<tbody>{rows.map((row, i) => <tr key={i}>{row.map((cell, j) => <td key={j}>{cell}</td>)}</tr>)}</tbody>
	</table></div>;
}

const C = ({ children }: { children: ComponentChildren }) => <code>{children}</code>;

/** A draft site page: a link within the app there, and to draft.ogreclan.com from ogreclan.com (which shows the guides) */
const Draft = ({ href, children }: { href: string, children: ComponentChildren }) =>
	HOME_HOST ? <a href={draftUrl(href)}>{children}</a> : <Link href={href}>{children}</Link>;

/** OgreClan's league template (Google Sheets): /copy asks to make your own copy */
const TEMPLATE = 'https://docs.google.com/spreadsheets/d/1q3jJUIIjkt_lFsUSXeZOOtHkNr08AGkSPETf91yqJUY';
/** The bot's Google account, which a league's sheet is shared with */
const BOT_ACCOUNT = 'ocdl-bot@ogerponbot.iam.gserviceaccount.com';
/** Who to ask: the site's admin, on Discord */
const ADMIN_DISCORD = 'supremeogerlord';

function Copyable({ text }: { text: string }) {
	return <><C>{text}</C> <button type="button" class="small" onClick={() =>
		void navigator.clipboard?.writeText(text).then(() => toast('Copied.'))}>Copy</button></>;
}
const go = (section: string, text: string) => <Link href={`/guides/${section}`}>{text}</Link>;

function Start() {
	const me = useMe();
	return <>
		<p>OgreClan's draft leagues have a few moving parts. This page explains each of them; pick a guide from the tabs above.</p>
		<Table head={['Part', "What it's for", 'Who uses it']} rows={[
			[<strong>draft.ogreclan.com</strong>, 'League pages: the tier list, picks, rosters, trades, matches and results, and the Draft Planner', 'Everyone'],
			[<strong>The Discord bot</strong>, <>Runs Discord draft leagues: <C>/setup</C>, <C>/pick</C>, <C>/trade</C>, <C>/postschedule</C> and the rest</>, 'Players and league moderators'],
			[<strong>play.ogreclan.com</strong>, <>OgreClan's Showdown server: league battles (with Ready), leagues run in a chat room (<C>/draft</C>), <C>/planner</C>, the kills and deaths tracker</>, 'Everyone'],
			[<strong>The league spreadsheet</strong>, `The league's template: prices, coaches, rosters and schedule. The bot reads it and writes picks into it`, `League staff. Players use the site's tier list instead`],
			[<strong>replay.ogreclan.com</strong>, 'Saved battles, to watch again or share', 'Everyone'],
		]} />

		<h2>Getting started</h2>
		<ol>
			<li>On draft.ogreclan.com, press <strong>Sign in with Discord</strong> (top right). Discord asks you to allow it the first time.
				{me && !me.user && <> <a href={HOME_HOST ? draftUrl('/account') : signInUrl('/account')}>Sign in now</a>.</>}</li>
			<li>Open your account page (your name, top right) and press <strong>Link my Showdown name</strong>. It shows a command
				like <C>/ogreclan link ABC123</C>.</li>
			<li>Log in on <a href="https://play.ogreclan.com">play.ogreclan.com</a> as the name you play as and type that command in any
				chat box. The code works once, for 10 minutes.</li>
		</ol>
		<p>Discord is who you are in the bot's leagues; your Showdown name is who you are in battles, Showdown leagues, replays
			and the planner. Link it once and both work everywhere.</p>
		<p>Can't sign in to Discord? A Global Admin can link your Showdown name for you and let you sign in with it. Then type{' '}
			<C>/ogreclan signin</C> on play.ogreclan.com (logged in as that name) and open the link it gives you. Each link works once,
			for 5 minutes, and signs in as you, so don't share it.</p>

		<h2>Which guide do I need?</h2>
		<ul>
			<li>Playing in a league on Discord: {go('bot', 'Bot: players')}, then {go('site', 'The website')} for the tier list,
				picks, trades and your matches.</li>
			<li>Running a league: {go('moderators', 'Bot: moderators')} and {go('sheet', 'The spreadsheet')}.</li>
			<li>Battling, the K/D tracker, the planner, or a league run in a Showdown chat room: {go('showdown', 'Showdown')}.</li>
			<li>Saved battles: {go('replays', 'Replays')}.</li>
		</ul>
		<p>Questions or problems: message <strong>@{ADMIN_DISCORD}</strong> on Discord.</p>
	</>;
}

function Site() {
	return <>
		<h2>Finding your league</h2>
		<p>The <Draft href="/league">Leagues</Draft> page lists every league you can see, with yours marked <strong>You're playing</strong>.</p>
		<Table head={['Kind', 'Run from', 'Its page']} rows={[
			['Discord league', <>The bot on Discord (<C>/setup</C>, <C>/pick</C>, <C>/trade</C>...), or made on this site</>, <>Under <strong>Discord leagues</strong>. It follows the bot within a couple of seconds.</>],
			['Showdown league', <>A chat room on play.ogreclan.com (<C>/draft</C>)</>, <>Under <strong>Your leagues</strong> or <strong>Other leagues</strong></>],
		]} />
		<p>A league marked <strong>Private</strong> only shows to its players and the site's staff: sign in with the Discord
			account you play with (Discord leagues), or link your Showdown name (Showdown leagues).</p>

		<h2>Drafting a Discord league</h2>
		<p>On your turn, pick on the site or on Discord. Either way the bot announces it, starts the next player's timer and
			updates the spreadsheet. The page shows who's on the clock and the time left, and refreshes by itself.</p>
		<ul>
			<li><strong>Making a pick:</strong> the <strong>It's your pick</strong> panel lists everything still available with its price.
				Search, or keep <strong>Only ones I can afford</strong> ticked. Press <strong>Pick</strong>, then <strong>Draft for</strong> to confirm.</li>
			<li><strong>Tera Captains</strong> (Tera formats): <strong>Tera Captain for</strong> drafts it at its Tera price, which comes out of both budgets.</li>
			<li><strong>Owed picks:</strong> if you were skipped, choose that round under <strong>Picking for</strong> to make it up.</li>
			<li><strong>Your pick queue:</strong> up to five Pokémon a round, in order. If your turn comes while you're away, the bot
				drafts the first one still available that you can afford, like <C>/queuepick</C>. Only you can see it.</li>
		</ul>
		<Table head={['Tab', "What's there"]} rows={[
			['Board', 'Every pick, round by round. Late and queued picks are marked.'],
			['Tier list', `The whole tier list, grouped by cost, with who has each Pokémon. Hide taken shows what's left. This is where to check prices: you never need to open the spreadsheet on your phone.`],
			['Rosters', 'Each player\'s Pokémon, points and Tera points left, and any owed rounds'],
			['Matches', 'Each week\'s matchups, your match, your team, Ready, and reporting results'],
			['Trades', 'Proposing trades, and every trade with when it takes effect'],
		]} />

		<h2>Trades</h2>
		<ul>
			<li>Propose a trade on the <strong>Trades</strong> tab (or with <C>/trade</C> on Discord): swap with a player, or choose
				<strong> Free agency</strong> to drop Pokémon back into the pool and pick up undrafted ones. Giving up nothing pays with the
				points you have left.</li>
			<li>A moderator approves or rejects it. <strong>Rosters change when the next week is posted</strong>, so nothing moves
				in the middle of a week.</li>
			<li>The league's limit counts the Pokémon you <em>receive</em>, not the ones you drop.</li>
			<li>For 24 hours after your pool's draft ends, trades are unlimited and don't count toward the limit. The bot pings
				everyone when it opens, and the league page shows the time left.</li>
		</ul>

		<h2>Playing your matches</h2>
		<ol>
			<li>Once the week is posted, open the <strong>Matches</strong> tab. Your match is at the top; choose another week from
				<strong> Your match in</strong>.</li>
			<li>Paste your team from the teambuilder (<strong>Export</strong>), or a pokepast.es link, into <strong>Your team for week
				N</strong> and press <strong> Save team</strong>. Showdown checks it against the league's format and your roster, and lists
				anything wrong. A Pokepaste link also becomes your Pokepaste for the week. Copy only the team from a Pokepaste page, not its
				title or notes, or use the link.</li>
			<li>Press <strong>Ready!</strong> You can do this before you log in to Showdown.</li>
			<li>When you're both ready and both online on <a href="https://play.ogreclan.com">play.ogreclan.com</a>, the battle starts
				there by itself with your saved teams, in the league's format. VGC leagues play best of 3 with closed team sheets; singles
				leagues play one game.</li>
			<li>When it ends, the result, each Pokémon's kills and deaths, and the replay are recorded by themselves.</li>
		</ol>
		<p>Ready battles need the league to use the website (staff turn this on with <C>/website</C>) and your Showdown name linked.
			A green dot means that player is online on play.ogreclan.com. <strong>Not ready</strong> takes it back.</p>

		<h3>Reporting results yourself</h3>
		<p>Games not played with Ready are reported on the Matches tab with <strong>Report a game</strong>, or on Discord (see
			{' '}{go('bot', 'Bot: players')}). Each player reports their own side; nobody has to confirm, and staff fix mistakes.</p>
		<ul>
			<li><strong>From the replay:</strong> paste the game's link from replay.ogreclan.com. It fills in both sides' kills and
				deaths and the winner.</li>
			<li><strong>By hand:</strong> type your Pokémon's kills and deaths for that game, and who won. Leave out Pokémon that didn't play.</li>
			<li><strong>Pokémon Champions:</strong> if you and your opponent both tick <strong>Play this match on Pokémon Champions</strong>,
				the match is played there instead. Each of you saves your Pokepaste and reports your own side by hand.</li>
		</ul>
		<p>The score shows in the week's list; click it for every game's kills and deaths. A 📋 next to a name is that player's
			Pokepaste for the match: you see your own straight away, and everyone's once the match is over. Give yours under your match
			(or with <C>/pokepaste</C> on Discord); after a match played with Ready, each team you didn't give a Pokepaste for goes up
			on Pokepaste by itself. Staff can report any match
			from a replay link, for its players (<strong>Staff: report from a replay</strong> under each week).</p>

		<h2>Hosting a league on the site</h2>
		<p>Anyone signed in can press <strong>Host a league</strong> on the Leagues page and apply. A league published here goes out
			under OgreClan's name, so hosts are approved first (by @{ADMIN_DISCORD}; you get a Discord message either way). Once
			approved, you make leagues that run on this site with the bot's rules, and you run them from their pages: start the draft,
			pick or edit picks for players, approve trades, post weeks and set the Showdown format. You don't need approval to run a
			league on Discord: invite the bot to your own server and use <C>/setup</C>.</p>
		<p>Site admins and draft moderators have <strong>Make a league</strong>, with the same settings as the bot's <C>/setup</C>:</p>
		<ul>
			<li><strong>Compatibility with bot on:</strong> the bot runs it on Discord, in the server and channels you choose, and the
				site is a second way in.</li>
			<li><strong>Off:</strong> it lives only on this site, still with the bot's rules (queues, timers, skips and trades).
				Start the draft, approve trades and post weeks from its page.</li>
		</ul>

		<h2>Showdown leagues</h2>
		<p>A Showdown league lives in a chat room on play.ogreclan.com, and its page here runs the same <C>/draft</C> commands
			for you; anything done on one shows on the other straight away.</p>
		<Table head={['Tab', "What's there"]} rows={[
			['Overview', 'Settings, phase and recent activity'],
			['Draft', 'The board and the clock; on your turn, what you can pick'],
			['Rosters', `Everyone's Pokémon and points; make your Tera Captains here`],
			['Pool', 'Every Pokémon with its points and who has it'],
			['Schedule', <>Each week's matches and results; <strong>Your match</strong> at the top, with Ready</>],
			['Standings', 'Wins, losses, kills and deaths'],
			['Trades', 'Trades and free-agent pickups (players, during the season)'],
			['My teams', 'Your team for each week, checked when you save it'],
			['Run the league', 'Settings, the pool, the draft and the season (room staff)'],
		]} />

		<h2>The Draft Planner</h2>
		<p><Draft href="/plan">Planner</Draft> (in the header) has type charts, coverage, speed tiers, move search, and your saved drafts
			and matchups. With your Showdown name linked they're kept with your account, and they're the same ones as <C>/planner</C>
			on play.ogreclan.com.</p>

		<h2>Problems</h2>
		<Table head={['Problem', 'What to do']} rows={[
			[`My league isn't on the Leagues page`, `It's probably private. Sign in with the Discord account you play with; for Showdown leagues, link your Showdown name.`],
			[`"You aren't playing in this draft"`, `You're signed in with a different Discord account. Sign out and back in with the right one.`],
			[`The tier list isn't available`, `The bot couldn't read the spreadsheet just then. Try again in a minute.`],
			['Ready! is greyed out', 'Save a usable team for that week first, and link your Showdown name.'],
			['I pressed Ready and nothing happened', `The battle starts when you're both ready and both online on play.ogreclan.com.`],
			['My team is refused', 'Fix the problems listed under it. A Mega has to be drafted as the Mega when the tier list lists it separately.'],
			[`A trade isn't on my roster yet`, `An approved trade takes effect once your match that week is over and you've given its Pokepaste (/pokepaste, or pokepaste: when you report a game). If your match was extended, it goes through when the next week is posted.`],
			['A pick is wrong', <>Fix your own with <C>/editpick</C> until the next pick is made; after that, ask a moderator.</>],
		]} />
	</>;
}

function BotPlayers() {
	return <>
		<p>The bot runs Discord leagues. Use these in your league's draft channel. Errors only ever show to you, and nothing is
			recorded until it's valid.</p>

		<h2>Drafting</h2>
		<Table head={['Command', 'What it does']} rows={[
			[<C>/pick round:1 pokemon:Great Tusk cost:18</C>, `Your pick, on your turn. The round has to be the one we're in and the cost has to match the tier list. Names autocomplete when a sheet is linked.`],
			[<C>/queuepick round:3 pokemon:Garchomp, Heatran, Skarmory</C>, `A backup list for a turn you might miss: up to 5, best first. The bot takes the first one still available. Leave out costs and it uses the tier list's.`],
			[<C>/myqueue</C>, `What you've queued (round:3 for one round). Only you see it.`],
			[<C>/cancelqueue</C>, `Throws a queue away before it fires (round:3 for one round). Costs nothing: no pick undone, no clock touched.`],
			[<C>/makeuppick round:3 pokemon:Garchomp cost:12</C>, 'Uses a turn you were skipped in. It must be used before your next turn.'],
			[<C>/editpick round:2 pokemon:Heatran cost:10</C>, `Fixes your own pick, until the next pick is made (a queued pick too). After that, ask a moderator.`],
			[<C>/finishdrafting</C>, `Stops early and keeps your team, once you're past the league's minimum. It asks you to confirm; only a moderator can put you back on the clock.`],
		]} />

		<h2>The timer</h2>
		<p>You each have your own timer, starting at the base time.</p>
		<ol>
			<li>Miss your turn and you're skipped, and <strong>your timer is halved</strong> (2m, then 1m, 30s, 15s). It doesn't go back up.</li>
			<li>You owe a makeup pick for that round: use <C>/makeuppick</C> before your next turn.</li>
			<li>If you don't, that next turn is skipped too and your timer halves again.</li>
		</ol>
		<p>Two things don't cost time: your queue being drafted out from under you, and missing both picks at the turn of the snake
			(your timer is only reduced once). Ask a moderator if a reduced timer looks wrong.</p>

		<h2>Looking things up</h2>
		<p>These reply only to you.</p>
		<Table head={['Command', 'What it shows']} rows={[
			[<C>/roster</C>, `Your team, costs, points spent and left, picks used. player:@someone for theirs.`],
			[<C>/status</C>, `Whose turn it is, the round, their time left, and owed makeup picks`],
			[<C>/progress</C>, 'Every pick so far, in order (round:3 for one round)'],
			[<C>/search pokemon:Garchomp</C>, `Whether it's still available, and its price`],
			[<C>/available cost:12</C>, 'Every undrafted Pokémon at that price'],
		]} />
		<p>The site's <strong>Tier list</strong> tab shows the whole list at once.</p>

		<h2>Trades, extensions and subs</h2>
		<Table head={['Command', 'What it does']} rows={[
			[<C>/trade drops:Garchomp gets:Heatran partner:@Alex</C>, `Proposes a trade, up to 5 each way. Without partner it's free agency (drop to the pool, pick up undrafted). drops:nil or 0 gives up nothing. A moderator approves it, and rosters change when the next week is posted.`],
			[<C>/trades</C>, 'Every trade and whether it was approved'],
			[<C>/extension reason:...</C>, `Asks for this week's match to be played next week. Either player can ask, and one week is the most it can move.`],
			[<C>/subrequest reason:...</C>, `Asks to sub out (if you're drafting) or offers to sub in (if you're not)`],
		]} />
		<p>The limit counts the Pokémon you pick up. Trades in the 24 hours after the draft ends don't count. The bot checks names,
			ownership and points before a moderator sees a trade; <C>/search</C> finds the right spelling.</p>

		<h2>Tera Captains (Tera formats only)</h2>
		<p>You have two budgets, your points and your Tera points, and at most 3 Tera Captains. Add <C>tera_captain:True</C> to a pick.
			A captain costs its <strong>Tera price</strong>, which replaces the normal price and comes out of <em>both</em> budgets:
			Great Tusk normally costs 18; as a captain, 17 from your points and 17 from your Tera points. A Pokémon with no Tera price
			is Tera banned. <C>/search</C> shows both prices.</p>

		<h2>Match results</h2>
		<p>Add <C>name:</C> (the league's name) when you're not in its draft channel.</p>
		<Table head={['Command', 'What it does']} rows={[
			[<C>/reportreplay week:1 game:1 replay:https://replay.ogreclan.com/...</C>, `Reports a game from its replay: both sides' kills and deaths, and the winner`],
			[<C>/reportgame week:1 game:1 kd:Garchomp 2-0, Rotom-Wash 1-1</C>, `Reports your own Pokémon's kills and deaths for a game; add winner:@someone`],
			[<C>/champions week:1</C>, `Agrees to play the match on Pokémon Champions (agree:False takes it back). It's on once both players agree.`],
			[<C>/pokepaste week:1 url:https://pokepast.es/...</C>, 'Your team for a match on Champions'],
			[<C>/matchresult week:1</C>, `What's been reported for a match (player:@someone for theirs)`],
		]} />
		<p>Games played with Ready on play.ogreclan.com are recorded by themselves. Each player reports their own side; staff fix mistakes.</p>
	</>;
}

function BotModerators() {
	const me = useMe();
	const staff = !!me?.user?.isModerator;
	return <>
		<p>For the people running Discord leagues. Moderators are anyone with Discord's <strong>Administrator</strong> permission,
			the server owner, or a role given access with <C>/draft-moderator role:@Draft Mods</C> (Administrator only; with no
			options it lists the roles, and <C>remove:True</C> takes one away).</p>

		<h2>Adding the bot to a server</h2>
		<p>In the Discord Developer Portal, <strong>OAuth2, URL Generator</strong>: tick the scopes <C>bot</C> and
			<C> applications.commands</C>, and the bot permissions <strong>View Channels</strong>, <strong>Send Messages</strong>,
			<strong> Embed Links</strong>, <strong>Read Message History</strong> and <strong>Add Reactions</strong> (for the 🇦/🇧 votes on
			posted matches). Open the link it makes to invite the bot. In private channels (the admin and draft channels), give the
			bot's role those permissions too. If it can't post in a draft channel, it pauses the clock rather than skipping anyone.</p>

		<h2>How it fits together</h2>
		<ul>
			<li><strong>The admin channel</strong> is where you run <C>/setup</C>. Trades, substitution and extension requests arrive
				here. One admin channel can run several leagues.</li>
			<li><strong>The draft channels</strong>, one per pool, are where players pick. You name them in <C>/setup</C>.</li>
			<li><strong>Names identify a league.</strong> Commands in the admin channel take <C>name:</C>; in a draft channel the bot
				works it out.</li>
		</ul>

		<h2>Creating a league: /setup (in the admin channel)</h2>
		<Table head={['Option', 'What it is']} rows={[
			[<C>name</C>, 'Unique, like OCDL Season 1'],
			[<C>draft_channel</C>, `Where pool A drafts`],
			[<><C>min_picks</C>, <C>max_picks</C></>, 'max_picks is the number of rounds (12 at most with a template)'],
			[<C>timer</C>, 'Base seconds per pick, like 600'],
			[<C>mode</C>, 'Full season (draft, trades and schedule), Draft + schedule (no trades), or Draft only (archives itself when the draft ends)'],
			[<><C>players</C> or <C>players_role</C></>, `@mentions in draft order, or a role the bot shuffles`],
			[<C>schedule_channel</C>, `Where each week's matchups are posted (or later, with /schedulechannel)`],
			[<C>budget</C>, `Points each; read from the template if you link one`],
			[<C>tera_budget</C>, 'Non-zero turns on Tera Captains (3 each at most)'],
			[<C>max_trades</C>, 'Pokémon each player may pick up in trades over the season; 0 is unlimited'],
			[<><C>template_link</C> or <C>tiering_link</C></>, <>The league's sheet: one, never both (see {go('sheet', 'The spreadsheet')})</>],
			[<><C>draft_channel_b</C>, <C>players_b</C></>, 'A second pool (needs a template)'],
			[<C>visibility</C>, 'Public (anyone sees it on the site) or Private (only its players). Change it with /visibility.'],
			[<C>website</C>, 'Whether the league uses draft.ogreclan.com: picks on the site, Ready battles, kills and deaths. On unless you say. Change it with /website.'],
			[<C>weeks</C>, 'Season length when the bot makes its own schedule'],
		]} />
		<p><C>/edit</C> changes the budget, picks, trade cap or document, until <C>/draft</C> runs.</p>

		<h2>Starting the draft (in the draft channel)</h2>
		<ul>
			<li><C>/draft</C> starts now: it pings the first player and starts their timer.</li>
			<li><C>/draft at:&lt;t:1760000000:F&gt;</C> starts it automatically then (make a timestamp at hammertime.cyou). Players can
				queue round 1 beforehand. A scheduled start survives a restart, and <C>/draft</C> starts it early.</li>
			<li>Two pools: run <C>/draft</C> in each pool's channel. They run at the same time, separately.</li>
		</ul>

		<h2>Fixing things mid-draft (in the draft channel)</h2>
		<Table head={['Command', 'What it does']} rows={[
			[<C>/pick</C>, 'Picks for whoever is on the clock'],
			[<C>/skipturn</C>, `Ends the current turn as if the timer ran out. penalty:False spares their timer; player:@Someone refuses unless that's who is up.`],
			[<C>/editpick round:3 pokemon:Garchomp cost:12 player:@Someone</C>, `Changes any pick, any time, or adds one for a round with nothing (which settles an owed makeup)`],
			[<C>/reopendrafting player:@Someone</C>, 'Puts someone who used /finishdrafting back on the clock'],
			[<C>/setskips player:@Someone skips:0</C>, 'Corrects their skips and recalculates their timer (timer:300 sets it exactly)'],
			[<C>/substitute out:@Leaving into:@Joining</C>, 'Hands a seat over: the roster, budgets and owed picks stay'],
			[<C>/removeplayer player:@Someone</C>, `Takes them out; their Pokémon go back unless keep_roster:True`],
			[<C>/cancelqueue player:@Someone</C>, `Deletes someone's queue before it fires (round:3 for one round). The channel is told.`],
		]} />
		{staff && <p class="notice"><strong>Staff only:</strong> <C>/myqueue</C> run in the league's <em>admin</em> channel gives you
			dropdowns to look up any player's queue. In a draft channel it only ever shows your own, so players don't know this is
			possible; keep it that way.</p>}

		<h2>Trades</h2>
		<Table head={['Command', 'What it does']} rows={[
			[<C>/approvetrade trade_id:3</C>, 'Approves it; rosters and budgets change at the next /postschedule'],
			[<C>/rejecttrade trade_id:3</C>, 'Declines it'],
			[<C>/unapprovetrade trade_id:3</C>, 'Takes back an approval while the trade is still waiting to apply'],
			[<C>/edittrade trade_id:3 drops:Garchomp gets:Rotom-Wash</C>, 'Fixes a nearly-right trade; it goes back to pending'],
			[<C>/trade ... for_player:@Someone</C>, 'Files a trade for a player (it still needs approving)'],
			[<C>/trades</C>, 'Every trade and its status'],
		]} />
		<p>Trade numbers are per league; add <C>name:</C> when the admin channel runs more than one. The bot refuses names that
			aren't on the tier list, Pokémon the proposer doesn't own, pickups already drafted and anything unaffordable. Whether a
			trade is <em>fair</em> is your call. Staff can also approve and reject on the site's Trades tab.</p>

		<h2>The season</h2>
		<Table head={['Command', 'What it does']} rows={[
			[<C>/postschedule week:3 name:...</C>, `Posts that week's matchups (one message each, with 🇦 and 🇧 to vote) and puts approved trades into effect. Reads the template's schedule or the bot's own. Never automatic, and there's no undo, so check the week first.`],
			[<C>/schedulechannel name:... channel:#schedule</C>, `Where the matchups go (the bot needs Add Reactions there)`],
			[<C>/reschedule name:... weeks:8</C>, `Rerolls the bot's own schedule`],
			[<C>/grantextension number:2</C>, `Grants an extension request (or player:@Someone to extend their match outright). /rejectextension and /extensions too.`],
			[<C>/cleargame week:1 game:2 player:@Someone</C>, `Removes a game's reported result so it can be reported again`],
		]} />
		<p>Staff can also post the next week and clear reported games on the site's Matches tab.</p>

		<h2>Settings any time</h2>
		<Table head={['Command', 'What it does']} rows={[
			[<C>/visibility name:... setting:Private</C>, 'Public or private on the site. Ogre Clan Official leagues are always public.'],
			[<C>/website name:... on:True</C>, 'Whether the league uses draft.ogreclan.com'],
		]} />
		<p><strong>Ogre Clan Official</strong> leagues (made by the bot's owner, or on the Ogre Clan Discord server) are public, and
			their players get Voice on play.ogreclan.com once they link their Showdown name.</p>

		<h2>Finishing up</h2>
		<Table head={['Command', 'What it does']} rows={[
			[<C>/archive name:...</C>, 'Closes a finished league and frees its channels. Rosters and history stay readable.'],
			[<C>/canceldraft name:... confirm:CANCEL</C>, 'Abandons a league. Nothing is deleted.'],
			[<C>/deleteleague name:...</C>, 'Deletes a league and everything in it (a backup is kept). Type the name again to confirm.'],
			[<C>/backup</C>, 'Saves a copy of the database (the bot also does this by itself)'],
		]} />
		<p>Errors show only to whoever typed the command; real events (picks, skips, approved trades) are posted publicly. If the bot
			can't post in a draft channel it pauses the clock rather than skipping anyone, and a restart never loses a draft.</p>

		<h2>Publishing a league on draft.ogreclan.com</h2>
		<p>Running a league through the bot needs nobody's permission. Making one on draft.ogreclan.com goes out under the site's
			name, so hosts are approved by the bot's owner first: message <strong>@{ADMIN_DISCORD}</strong> on Discord.</p>
	</>;
}

function Sheet() {
	return <>
		<p class="notice">For the people who set up and run leagues. <strong>Players:</strong> you don't need the spreadsheet. Every
			league's tier list is on its page on this site, under <strong>Tier list</strong>, made to open easily on a phone.</p>

		<div class="card" style="margin:12px 0">
			<h3 style="margin-top:0">The league template</h3>
			<p>OgreClan's template has everything a league needs: the tier list, coaches, rosters and schedule. Make your own copy for
				each league, and link the copy in <C>/setup</C>.</p>
			<div class="row">
				<a class="button primary" href={`${TEMPLATE}/copy`} target="_blank" rel="noreferrer">Make a copy</a>
				<a class="button" href={`${TEMPLATE}/edit?usp=sharing`} target="_blank" rel="noreferrer">Open the template</a>
			</div>
			<p style="margin-bottom:0">Then share your copy with the bot as <strong>Editor</strong>: <Copyable text={BOT_ACCOUNT} /></p>
		</div>

		<h2>Three ways to run a league</h2>
		<p>Link at most one document in <C>/setup</C>.</p>
		<Table head={['', 'No document', 'Tiering sheet', 'Template']} rows={[
			['Draft, timers, makeup picks, trades', 'Yes', 'Yes', 'Yes'],
			['Prices checked, names autocompleted', '—', 'Yes', 'Yes'],
			[<><C>/search</C>, <C>/available</C>, the site's tier list</>, '—', 'Yes', 'Yes'],
			['Tera Captains', 'Yes (you type the price)', 'Yes', 'Yes'],
			['Rosters written to the sheet', '—', '—', 'Yes'],
			['Settings read from the sheet', '—', '—', 'Yes'],
			['Two pools', '—', '—', 'Yes'],
			[<C>/postschedule</C>, 'Bot makes the fixtures', 'Bot makes the fixtures', 'Read from the sheet'],
		]} />
		<p>A sheet that stops responding never blocks a pick: the draft carries on, and the bot's database is always the source of truth.</p>

		<h2>A tiering sheet</h2>
		<p>The simplest document. Row 1 is headings (ignored); from row 2, column <strong>A</strong> is the Pokémon, <strong>B</strong> its
			price and <strong>C</strong> its Tera price.</p>
		<Table head={['', 'A', 'B', 'C']} rows={[['1', '(headings)', '', ''], ['2', 'Great Tusk', '18', '17'], ['3', 'Garchomp', '17', '']]} />
		<ul>
			<li>A Pokémon is <strong>banned</strong> when B is blank, holds text (B, Banned, -) or a placeholder like 99 or 999.</li>
			<li>A Pokémon with no Tera price is <strong>Tera banned</strong>: draftable, but never a Tera Captain.</li>
			<li>Names are kept in the sheet's own spelling, so the bot files <C>rotom-mow</C> as Rotom-Mow.</li>
		</ul>

		<h2>The league template</h2>
		<p><a href={`${TEMPLATE}/copy`} target="_blank" rel="noreferrer">Make your own copy of the template</a> (or open it and use File,
			Make a copy). <C>/setup</C> reads the league's settings from its
			<strong> named ranges</strong>, so the template can move them around:</p>
		<Table head={['Named range', 'Used for']} rows={[
			[<C>Budget</C>, 'Points each coach may spend'],
			[<><C>TeraBudget</C>, <C>TeraFormat</C></>, 'Tera points, and whether it is a Tera format'],
			[<C>NumPools</C>, '1 or 2'],
			[<C>TieringSheet</C>, 'Which tab holds the price list (point it at another tab to re-price)'],
			[<C>SeasonWeeks</C>, 'Season length'],
		]} />
		<p>Because the template sets the budget, <C>/setup</C> doesn't ask for one, and refuses an option that contradicts the sheet.</p>

		<h3>Who is in the draft: the Coaches tab</h3>
		<ul>
			<li><strong>AC, "In schedule":</strong> only ticked rows take part, and a tick reserves that coach's roster block, so gaps are kept.</li>
			<li><strong>Y, "Pool":</strong> B for pool B; anything else is pool A.</li>
			<li><strong>B, "Username":</strong> if a ticked row already names someone, the bot matches a player to it by Discord name, so
				the order you @mention people in doesn't have to match the sheet. A row naming someone you didn't list is never given to
				someone else: <C>/setup</C> tells you instead.</li>
		</ul>
		<h3>What the bot writes</h3>
		<p>Only two things; everything else is formulas reacting to them:</p>
		<ul>
			<li>Usernames and pool letters in <C>Coaches!B</C> and <C>Coaches!Y</C>, only in rows it owns.</li>
			<li>One Pokémon name per round in each roster block, plus the Tera Captain tick box in a Tera format (on the
				<strong> Tera Rosters</strong> tab; otherwise <strong>Rosters</strong>). Blocks are 12 rows, so a template league has at most 12 rounds.</li>
		</ul>

		<h2>Connecting it</h2>
		<ol>
			<li><a href={`${TEMPLATE}/copy`} target="_blank" rel="noreferrer">Make a copy of the template</a> (or use your own tiering sheet).</li>
			<li>Share your copy (Share, top right) with the bot's Google account as <strong>Editor</strong>: <Copyable text={BOT_ACCOUNT} /></li>
			<li>Link it in <C>/setup</C> with <C>template_link:</C> or <C>tiering_link:</C>, never both.</li>
			<li>Run <C>/inspectsheet</C> before the first real draft. It only reads, and reports the tabs, the settings it found, which
				tiering tab is in use, how many Pokémon are draftable or banned, and where picks would be written.</li>
		</ol>
		<p><C>/sync</C> rewrites the coach list and every roster block from the bot's database, if the sheet ever drifts. A missing
			tab is named exactly rather than written to the wrong place.</p>

		<h2>Tera Captains on the sheet</h2>
		<p>A captain costs its Tera price from both budgets, replacing the normal price. With a 115 budget and 28 Tera points, and
			Great Tusk at 18 (Tera 17): drafted normally, 97 points left and 28 Tera; as a captain, 98 left and 11 Tera. That's exactly
			what the template's own formulas do.</p>
	</>;
}

function Showdown() {
	return <>
		<p><a href="https://play.ogreclan.com">play.ogreclan.com</a> is OgreClan's own Showdown server. Log in with your usual Showdown name.</p>

		<h2>League battles</h2>
		<ul>
			<li>Discord league matches start from <strong>Ready</strong> on the site (see {go('site', 'The website')}); Showdown league
				matches from <strong>Ready!</strong> on the match page (<C>/draft ready 1</C>). A normal <C>/challenge</C> isn't recorded
				as a league match.</li>
			<li><strong>VGC Draft</strong> formats ("[format] (VGC Draft)" in the format list) are closed team sheets and always best of 3.
				VGC league matches are played that way.</li>
			<li>League players get Voice (+) on the server while their league has it turned on.</li>
		</ul>

		<h2>Private battles</h2>
		<p>Battles made private (<C>/hidenext</C> before a battle, <C>/hideroom</C> during one, or the client's privacy option) have no spectators: only their players and Global Admins
			can join, even with the link. They're never announced in the Lobby. Their replays are unlisted (see {go('replays', 'Replays')}).</p>

		<h2>Kills and deaths</h2>
		<p>The table is added to the battle when it ends, so the replay has it. Best-of sets get totals for the whole set too.</p>
		<Table head={['Command', 'What it does']} rows={[
			[<>The box at the start of a battle, or <C>/kdtracker on</C></>, 'Tracks this battle (players; off stops it)'],
			[<C>/kdtracker always on</C>, 'Tracks every battle you play'],
			[<C>/kdtracker room on</C>, `Tracks the room's tournaments, challenges sent in it, and battles between two people both in it (Room Owners)`],
			[<C>/kdtracker room [room] on</C>, 'The same for another room, from anywhere (Room Owners of it, Global Admins)'],
			[<C>/kdtracker server on</C>, 'Tracks every battle on the server (Global Admins)'],
		]} />
		<p>League matches are always tracked. Global Admins can turn tracking on or off in any battle.</p>

		<h2>The Draft Planner: /planner</h2>
		<p>Type <C>/planner</C> (or <C>/dp</C>) in a chat room or battle and it opens there as a panel only you can see: type chart,
			coverage, speed tiers and move search, with as many named drafts and matchups as you like, the same ones as on this
			site's <Draft href="/plan">Planner</Draft>.</p>
		<Table head={['Command', 'What it does']} rows={[
			[<C>/planner page</C>, 'Opens it as a page, every section at once'],
			[<><C>/planner draft [name]</C>, <C>/planner matchup [name]</C></>, 'Opens one of your drafts or matchups'],
			[<C>/planner new draft, [name]</C>, 'Starts a new draft (or new matchup)'],
			[<C>/planner league [room]</C>, `Your drafts and matchups in that room's league`],
			[<><C>/planner add [Pokémon]</C>, <C>/planner remove [Pokémon]</C></>, 'Changes the open draft (planning never drafts anything)'],
			[<C>/planner import</C>, `Saves a teambuilder export as a draft, or pastes your opponent's into a matchup`],
		]} />

		<h2>Leagues in a Showdown chat room: /draft</h2>
		<p>Any chat room can run one league. Room staff (% and up) move it through four phases: <strong>Setup</strong>
			(<C>/draft setup</C>: format, settings, players and the pool), <strong>Draft</strong> (<C>/draft start</C>),
			<strong> Season</strong> (<C>/draft end</C> makes a round-robin schedule) and <strong>Ended</strong> (<C>/draft endseason</C>).
			Every draft page has buttons along the top to move between the league's pages. The same league is on this site.</p>
		<h3>Everyone</h3>
		<Table head={['Command', 'What it does']} rows={[
			[<C>/draft</C>, 'Opens the league: the board during the draft, the Home page otherwise'],
			[<><C>/draft board</C>, <C>/draft search [text]</C></>, 'The draft board, searched for a Pokémon if you like'],
			[<><C>/draft rosters</C>, <C>/draft schedule</C>, <C>/draft standings</C>, <C>/draft pool</C></>, `The league's pages`],
			[<C>/draft match [week]</C>, `A week's match page`],
			[<C>/drafthelp</C>, 'The command list'],
		]} />
		<h3>Players</h3>
		<Table head={['Command', 'What it does']} rows={[
			[<C>/draft pick [Pokémon]</C>, `Drafts on your turn, or as a pick you're owed`],
			[<><C>/draft roster</C>, <C>/draft teams</C></>, 'My Roster, and My Teams (a slot per week to paste your team)'],
			[<C>/draft captain [Pokémon]</C>, 'Makes it a Tera Captain, or stops it being one (any time)'],
			[<C>/draft trade [user] | [yours] | [theirs]</C>, 'Offers a trade; they accept, then staff approve it'],
			[<C>/draft freeagent [drop] | [pick up]</C>, 'Swaps for an undrafted Pokémon straight away'],
			[<><C>/draft ready [week]</C>, <C>/draft unready [week]</C></>, 'Readies up for a match; it starts when both are ready and online'],
		]} />
		<h3>Room staff</h3>
		<Table head={['Command', 'What it does']} rows={[
			[<C>/draft setup</C>, 'Makes a league in the room, or opens its setup page'],
			[<C>/draft format [format or challenge code]</C>, 'Sets the format, before the draft'],
			[<><C>/draft addplayers [users]</C>, <C>/draft removeplayer</C>, <C>/draft renameplayer [old], [new]</C>, <C>/draft moveplayer</C></>, 'The players and the order'],
			[<C>/draft generatepool</C>, 'Every Pokémon the format allows, priced automatically'],
			[<C>/draft importpool [CSV]</C>, 'Replaces the pool with a CSV or cells pasted from a spreadsheet'],
			[<><C>/draft setpoints [points], [Pokémon]</C>, <C>/draft removepool</C>, <C>/draft autopoints</C></>, 'Edits prices and the pool'],
			[<><C>/draft start</C>, <C>/draft skip</C>, <C>/draft undo</C>, <C>/draft forcepick [user], [Pokémon]</C></>, 'Runs the draft'],
			[<><C>/draft end</C>, <C>/draft week [number or next]</C></>, 'Starts the season, and moves it on a week'],
			[<C>/draft trade approve [id]</C>, 'Approves a trade both players agreed to (or reject)'],
			[<><C>/draft report [week], [user], [2-1]</C>, <C>/draft clearresult</C></>, 'Enters or clears a result by hand'],
			[<><C>/draft addmon</C>, <C>/draft removemon</C></>, 'Fixes a roster'],
			[<><C>/draft endseason</C>, <C>/draft delete</C>, <C>/draft restore</C></>, 'Ends, deletes (a copy is kept) or brings back the league'],
		]} />
		<p>Kills count the way you'd expect: the attacker that knocks out the foe; for indirect damage, whoever caused it (hazards,
			poison, burn, Leech Seed, Rocky Helmet...); for recoil, the last foe that damaged it. Standings rank by wins, then fewest
			losses, then kill differential, then game differential.</p>
	</>;
}

function Replays() {
	return <>
		<p>Battles saved on play.ogreclan.com are at <a href="https://replay.ogreclan.com">replay.ogreclan.com</a>.</p>
		<h2>Saving one</h2>
		<ul>
			<li>Discord league matches are saved by themselves when they end.</li>
			<li>Anything else: <strong>Save replay</strong> (or <C>/savereplay</C>) in the battle, during it or after. It needs voice (+)
				or higher, globally or in a public room. You get the link straight away.</li>
		</ul>
		<h2>Public and unlisted</h2>
		<p>Replays that aren't public work like unlisted videos:</p>
		<ul>
			<li>They're left out of the recent list and searches.</li>
			<li>Anyone with the link can watch, so share it with whoever you like.</li>
			<li>Their players, signed in on draft.ogreclan.com with their Showdown name linked, see them under <strong>Your replays</strong>
				with the link to copy, and can watch them without it.</li>
		</ul>
		<p>Private battles' replays are unlisted.</p>
		<h2>Deleting one</h2>
		<p>Signed in, a replay's players get a <strong>Delete</strong> button on it and in their list. Once deleted, nobody can watch it.
			Admins can see and delete anyone's.</p>
	</>;
}

function Staff() {
	return <>
		<p>Site staff come from ranks on play.ogreclan.com; ranks on the main Showdown server don't count, and neither do group
			chats. A rank change reaches the site within a minute.</p>
		<Table head={['Role', 'Who', 'What they can do']} rows={[
			['Admin', 'The pinned admin Discord account, and linked Showdown accounts that are Global Admin (~) or Room Owner (#) of any room',
				'Everything below, plus the Admin page, the voice setting, and archiving or deleting leagues'],
			['Global Admin', 'Global Admin (~) on play.ogreclan.com, and the pinned admin account', `Everything an admin can, plus linking a Showdown name to someone's account`],
			['Draft moderator', 'Linked Showdown accounts that are Global or Room Moderator (@)',
				'See every league, private ones too; act as staff in every league; make leagues on the site'],
		]} />

		<h2>On the site</h2>
		<ul>
			<li><strong>Pick for</strong> whoever is on the clock, and <strong>edit any pick</strong> (click a square on the Board).</li>
			<li>Approve or reject trades on the <strong>Trades</strong> tab; post the next week, set the Showdown format and clear
				reported games on <strong>Matches</strong>.</li>
			<li>Admins: <strong>voice</strong> for a league's players (global voice, and voice in its division room), and
				<strong> Archive</strong> or <strong>Delete</strong> a league.</li>
			<li><strong>Who hasn't linked</strong> (Admin and Leagues pages): every division's players who haven't signed in, or haven't
				linked their Showdown name, with <strong>Copy reminder</strong> to paste in the league's Discord channel (it @mentions them).</li>
			<li><strong>View as</strong> (Admin page): see the site exactly as that person does (their leagues, picks queue, matches and
				replays). It's read-only, and the bar at the top has <strong>Stop viewing</strong>.</li>
			<li>The <Draft href="/admin">Admin</Draft> page lists every account. Unlink a Showdown name, sign someone out everywhere, and
				(Global Admins) link a Showdown name for someone by typing it, with no code needed. Check the name first: the account
				plays as that name and carries its rank.</li>
		</ul>

		<h2>On play.ogreclan.com</h2>
		<ul>
			<li>Global Admins have the console: <C>/eval</C>, <C>/hotpatch</C> and the rest.</li>
			<li>Global Admins can join private battles (they're in the battle list for them, and <C>/join</C> works without the
				password), and control the kills and deaths tracker anywhere (see {go('showdown', 'Showdown')}).</li>
			<li>Room chat is logged, for moderation.</li>
		</ul>
	</>;
}

export function GuidesPage({ section }: { section: string }) {
	const current = SECTIONS.find(([id]) => id === section)?.[0] ?? 'start';
	const title = SECTIONS.find(([id]) => id === current)![1];
	return <div class="guides">
		<h1>Guides</h1>
		<nav class="tabs">{SECTIONS.map(([id, name]) => <Link key={id} href={`/guides/${id}`} class={id === current ? 'active' : ''}>{name}</Link>)}</nav>
		<div class="card">
			{current !== 'start' && <h2 style="margin-top:0">{title}</h2>}
			{current === 'start' && <Start />}
			{current === 'site' && <Site />}
			{current === 'bot' && <BotPlayers />}
			{current === 'moderators' && <BotModerators />}
			{current === 'sheet' && <Sheet />}
			{current === 'showdown' && <Showdown />}
			{current === 'replays' && <Replays />}
			{current === 'staff' && <Staff />}
		</div>
	</div>;
}
